import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Edit,
  Key,
  Layers,
  Plus,
  RefreshCw,
  Save,
  Search,
  Shield,
  UserCheck,
  Users,
  UserX,
  Mail,
} from "lucide-react"

import { SEO } from "@/components/SEO"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "@/components/ui/use-toast"
import { getApiBaseUrl, readApiJson } from "@/lib/api"

type UserStatus = "active" | "inactive" | "suspended"

type Role = {
  id: number
  name: string
  slug: string
  description: string | null
  status: "active" | "inactive"
  isDefault: boolean
  isSuperAdmin: boolean
  userCount?: number
  permissions?: Permission[]
}

type User = {
  id: number
  tenant_id?: number | null
  tenantId?: number | null
  tenant_name?: string | null
  tenantName?: string | null
  tenant_slug?: string | null
  email: string
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  display_name?: string | null
  employee_code?: string | null
  display_code?: string | null
  role?: string
  roles: Role[]
  status: UserStatus
  last_login_at?: string | null
  lastLoginAt?: string | null
  created_at?: string | null
  createdAt?: string | null
  $extras?: {
    roles?: Role[]
    primaryRole?: Role | null
  }
}

type Resource = {
  id: number
  name: string
  slug: string
  type: "page" | "tab" | "field"
  parentId: number | null
  level: number
  sortOrder: number
  status: string
  permissions?: PermissionFlags
}

type Permission = {
  resource?: { slug?: string }
  canView?: boolean
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
  canImport?: boolean
  canExport?: boolean
  isMandatory?: boolean
}

type PermissionFlags = {
  can_view?: boolean
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
  can_import?: boolean
  can_export?: boolean
  is_mandatory?: boolean
}

type Tenant = {
  id: number
  name: string
  slug: string
}

type UsersMeta = {
  total: number
  currentPage: number
  perPage: number
  statusCounts: {
    active: number
    inactive: number
    suspended: number
  } | null
}

const selectClassName = "h-10 rounded-md border border-input bg-background px-3 text-sm"

function getMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

function toBoolean(value: unknown) {
  return value === true || value === 1 || value === "1" || value === "true"
}

function normalizeRoleName(value: string) {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, "")
}

function isProtectedRole(role: Role | null) {
  if (!role) return false
  const normalized = normalizeRoleName(role.name)
  return role.isDefault || role.isSuperAdmin || ["superadmin", "admin", "manager", "employee"].includes(normalized)
}

function getRoleSlug(name: string, fallback?: string | null) {
  return fallback || name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
}

function formatDate(value?: string | null) {
  if (!value) return "Never"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).formatToParts(date)

  const day = parts.find((p) => p.type === "day")?.value ?? ""
  const month = parts.find((p) => p.type === "month")?.value ?? ""
  const year = parts.find((p) => p.type === "year")?.value ?? ""

  return `${day}-${month}-${year}`
}

function getUserName(user: User) {
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim()
  return user.display_name || user.fullName || fullName || user.email
}

function getEmployeeCode(user: User) {
  return user.employee_code || user.display_code || ""
}

function getTenantName(user: User, tenants: Tenant[]) {
  if (user.tenant_name || user.tenantName) return user.tenant_name || user.tenantName
  const tenantId = Number(user.tenant_id || user.tenantId)
  const tenant = tenants.find((item) => item.id === tenantId)
  return tenant?.name || user.tenant_slug || "Unknown Tenant"
}

function getInitial(user: User) {
  return (user.display_name?.[0] || user.firstName?.[0] || user.fullName?.[0] || user.email[0] || "U").toUpperCase()
}

function extractArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === "object") {
    const value = payload as Record<string, unknown>
    if (Array.isArray(value.data)) return value.data
    if (Array.isArray(value.roles)) return value.roles
    if (value.data && typeof value.data === "object") return extractArray(value.data)
  }
  return []
}

function normalizeRole(raw: unknown): Role {
  const item = raw as Record<string, unknown>
  const name = String(item.name || "Untitled Role")
  return {
    id: Number(item.id),
    name,
    slug: getRoleSlug(name, typeof item.slug === "string" ? item.slug : null),
    description: typeof item.description === "string" ? item.description : null,
    status: item.status === "inactive" ? "inactive" : "active",
    isDefault: toBoolean(item.isDefault ?? item.is_default),
    isSuperAdmin: toBoolean(item.isSuperAdmin ?? item.is_super_admin),
    userCount: typeof item.userCount === "number" ? item.userCount : typeof item.user_count === "number" ? item.user_count : undefined,
    permissions: Array.isArray(item.permissions) ? (item.permissions as Permission[]) : undefined,
  }
}

function normalizeUser(raw: unknown): User {
  const item = raw as User & {
    employeeCode?: string | null
    displayCode?: string | null
  }

  return {
    ...item,
    id: Number(item.id),
    employee_code: item.employee_code || item.employeeCode || null,
    display_code: item.display_code || item.displayCode || null,
    status: item.status || "inactive",
    role: item.role || "employee",
    roles: item.$extras?.roles || item.roles || [],
  }
}

function flattenResources(items: unknown[], parentId: number | null = null, fallbackLevel = 0): Resource[] {
  const out: Resource[] = []

  items.forEach((raw) => {
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    const level = typeof item.level === "number" ? item.level : fallbackLevel
    const resource: Resource = {
      id,
      name: String(item.name || "Untitled Resource"),
      slug: String(item.slug || `resource-${id}`),
      type: item.type === "field" || item.type === "tab" ? item.type : "page",
      parentId: Number(item.parentId ?? item.parent_id) || parentId,
      level,
      sortOrder: Number(item.sortOrder ?? item.sort_order ?? 0),
      status: String(item.status || "active"),
      permissions: item.permissions as PermissionFlags | undefined,
    }

    out.push(resource)

    if (Array.isArray(item.children)) {
      out.push(...flattenResources(item.children, id, level + 1))
    }
  })

  return out
}

function buildHeaders() {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }

  const token = localStorage.getItem("auth_token") || localStorage.getItem("token") || localStorage.getItem("access_token")
  if (token) headers.Authorization = `Bearer ${token}`

  const params = new URLSearchParams(window.location.search)
  const tenantSlug =
    params.get("tenant_slug") ||
    localStorage.getItem("current_tenant_slug") ||
    localStorage.getItem("tenant_slug")
  const tenantId = localStorage.getItem("tenant_id")

  if (tenantSlug) headers["X-Tenant-Slug"] = tenantSlug
  if (tenantId) headers["X-Tenant-ID"] = tenantId

  return headers
}

export function UserManagementPage() {
  const API_BASE = getApiBaseUrl()

  const [activeSubTab, setActiveSubTab] = useState("users")
  const [users, setUsers] = useState<User[]>([])
  const [allFilteredUsers, setAllFilteredUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [usersMeta, setUsersMeta] = useState<UsersMeta>({
    total: 0,
    currentPage: 1,
    perPage: 10,
    statusCounts: null,
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [roleFilter, setRoleFilter] = useState("all")
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [newRoleName, setNewRoleName] = useState("")
  const [newRoleDescription, setNewRoleDescription] = useState("")
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [newUser, setNewUser] = useState({
    tenantId: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    status: "active" as UserStatus,
    isSuperAdmin: true,
  })
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [welcomeSending, setWelcomeSending] = useState(false)
  const [welcomeSearch, setWelcomeSearch] = useState("")
  const [permissionsMatrix, setPermissionsMatrix] = useState<Record<string, PermissionFlags>>({})
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const fetchJson = useCallback(
    async (path: string, init?: RequestInit, fallback = "Request failed.") => {
      const response = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...buildHeaders(),
          ...(init?.headers || {}),
        },
      })
      return readApiJson(response, fallback)
    },
    [API_BASE]
  )

  const loadUsers = useCallback(
    async (page = 1) => {
      setUsersLoading(true)
      setApiError(null)
      try {
        const json = await fetchJson("/tenants?page=1&limit=1000&include_users=1", undefined, "Failed to load users.")
        const tenantRows = extractArray(json)
          .map((raw) => {
            const row = raw as Record<string, unknown>
            const item = (row.$attributes && typeof row.$attributes === "object"
              ? { ...(row.$attributes as Record<string, unknown>), users: row.users }
              : row) as Record<string, unknown>
            return {
              id: Number(item.id),
              name: String(item.name || "Untitled Tenant"),
              slug: String(item.slug || ""),
              users: Array.isArray(item.users) ? item.users : [],
            }
          })
          .filter((tenant) => Number.isFinite(tenant.id))

        const allUsers = tenantRows.flatMap((tenant) =>
          tenant.users.map((rawUser) =>
            normalizeUser({
              ...(rawUser as Record<string, unknown>),
              tenant_id: (rawUser as Record<string, unknown>).tenant_id || tenant.id,
              tenant_name: (rawUser as Record<string, unknown>).tenant_name || tenant.name,
              tenant_slug: (rawUser as Record<string, unknown>).tenant_slug || tenant.slug,
            })
          )
        )

        const query = searchTerm.trim().toLowerCase()
        const filteredUsers = allUsers.filter((user) => {
          const matchesSearch =
            !query ||
            [
              getUserName(user),
              getEmployeeCode(user),
              user.email,
              getTenantName(user, tenantRows),
              user.roles.map((role) => role.name).join(" "),
            ]
              .join(" ")
              .toLowerCase()
              .includes(query)

          const matchesStatus = statusFilter === "all" || user.status === statusFilter
          const matchesRole = roleFilter === "all" || user.roles.some((role) => role.slug === roleFilter)

          return matchesSearch && matchesStatus && matchesRole
        })

        const perPage = 10
        const start = (page - 1) * perPage
        const pageRows = filteredUsers.slice(start, start + perPage)
        const statusCounts = allUsers.reduce(
          (acc, user) => {
            if (user.status === "active" || user.status === "inactive" || user.status === "suspended") {
              acc[user.status] += 1
            }
            return acc
          },
          { active: 0, inactive: 0, suspended: 0 }
        )

        setTenants(tenantRows.map(({ id, name, slug }) => ({ id, name, slug })))
        setAllFilteredUsers(filteredUsers)
        setUsers(pageRows)
        setRoles((currentRoles) => {
          const merged = new Map(currentRoles.map((role) => [role.id, role]))
          allUsers.forEach((user) => {
            user.roles.forEach((role) => {
              if (Number.isFinite(role.id)) merged.set(role.id, normalizeRole(role))
            })
          })
          return Array.from(merged.values())
        })
        setUsersMeta({
          total: filteredUsers.length,
          currentPage: page,
          perPage,
          statusCounts,
        })
      } catch (error) {
        setUsers([])
        setAllFilteredUsers([])
        setApiError(getMessage(error, "Failed to load users."))
      } finally {
        setUsersLoading(false)
      }
    },
    [fetchJson, roleFilter, searchTerm, statusFilter]
  )

  const loadRoles = useCallback(async () => {
    setRolesLoading(true)
    setRoleError(null)
    try {
      const json = await fetchJson("/roles?page=1&limit=1000", undefined, "Failed to load roles.")
      setRoles(extractArray(json).map(normalizeRole).filter((role) => Number.isFinite(role.id)))
    } catch (error) {
      setRoleError(getMessage(error, "Failed to load roles."))
    } finally {
      setRolesLoading(false)
    }
  }, [fetchJson])

  const loadTenants = useCallback(async () => {
    try {
      const json = await fetchJson("/tenants?page=1&limit=1000", undefined, "Failed to load tenants.")
      const rows = extractArray(json)
        .map((raw) => {
          const item = raw as Record<string, unknown>
          return {
            id: Number(item.id),
            name: String(item.name || "Untitled Tenant"),
            slug: String(item.slug || ""),
          }
        })
        .filter((tenant) => Number.isFinite(tenant.id))

      setTenants(rows)
      setNewUser((current) => ({
        ...current,
        tenantId: current.tenantId || rows[0]?.id.toString() || "",
      }))
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to load tenants."),
        variant: "destructive",
      })
    }
  }, [fetchJson])

  const loadResources = useCallback(async () => {
    try {
      let json = await fetchJson("/resources", undefined, "Failed to load resources.")
      let items = extractArray(json)

      if (items.length === 0) {
        json = await fetchJson("/roles/resources", undefined, "Failed to load resources.")
        items = extractArray(json)
      }

      setResources(flattenResources(items).sort((a, b) => a.level - b.level || a.sortOrder - b.sortOrder))
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to load resources."),
        variant: "destructive",
      })
    }
  }, [fetchJson])

  useEffect(() => {
    loadTenants()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (activeSubTab === "roles") loadRoles()
    if (activeSubTab === "permissions") {
      loadRoles()
      loadResources()
    }
  }, [activeSubTab, loadRoles, loadResources])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadUsers(1)
    }, 500)

    return () => window.clearTimeout(timer)
  }, [loadUsers])

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) || null,
    [roles, selectedRoleId]
  )

  const totalPages = Math.max(1, Math.ceil(usersMeta.total / usersMeta.perPage))

  const activeCount = usersMeta.statusCounts?.active ?? users.filter((u) => u.status === "active").length
  const inactiveCount = usersMeta.statusCounts?.inactive ?? users.filter((u) => u.status === "inactive").length

  async function updateUserStatus(user: User) {
    const nextStatus: UserStatus = user.status === "active" ? "inactive" : "active"
    try {
      await fetchJson(
        `/owner/users/${user.id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: nextStatus }),
        },
        "Failed to update user status."
      )
      toast({
        title: "Success",
        description: `User ${nextStatus === "active" ? "activated" : "deactivated"} successfully.`,
      })
      loadUsers(usersMeta.currentPage)
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to update user status."),
        variant: "destructive",
      })
    }
  }

  function resetUserForm() {
    setEditingUser(null)
    setNewUser({
      tenantId: tenants[0]?.id.toString() || "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      status: "active",
      isSuperAdmin: true,
    })
  }

  function openCreateUserForm() {
    resetUserForm()
    setShowUserForm(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  function openEditUserForm(user: User) {
    const isSuperAdmin = user.role === "super_admin" || user.role === "superadmin" || user.roles.some((role) => role.isSuperAdmin || role.slug === "super-admin")
    setEditingUser(user)
    setNewUser({
      tenantId: String(user.tenant_id || user.tenantId || ""),
      firstName: user.firstName || (user as any).first_name || "",
      lastName: user.lastName || (user as any).last_name || "",
      email: user.email,
      password: "",
      status: user.status,
      isSuperAdmin,
    })
    setShowUserForm(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  async function saveUser() {
    const tenantId = Number(newUser.tenantId)
    const firstName = newUser.firstName.trim()
    const lastName = newUser.lastName.trim()
    const email = newUser.email.trim()

    if (!Number.isFinite(tenantId)) {
      setApiError("Please select a tenant.")
      return
    }

    if (!email || (!editingUser && !newUser.password)) {
      setApiError(editingUser ? "Email is required." : "Email and password are required.")
      return
    }

    try {
      const payload = {
        tenantId,
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        email,
        password: newUser.password,
        status: newUser.status,
        isSuperAdmin: newUser.isSuperAdmin,
      }

      await fetchJson(
        editingUser ? `/owner/users/${editingUser.id}` : "/owner/users",
        {
          method: editingUser ? "PUT" : "POST",
          body: JSON.stringify(payload),
        },
        editingUser ? "Failed to update user." : "Failed to create user."
      )

      toast({
        title: "Success",
        description: editingUser ? "User updated successfully." : `${newUser.isSuperAdmin ? "Super Admin" : "User"} created successfully.`,
      })
      setShowUserForm(false)
      resetUserForm()
      loadUsers(1)
    } catch (error) {
      setApiError(getMessage(error, editingUser ? "Failed to update user." : "Failed to create user."))
    }
  }

  async function resetPassword(userIds: number[]) {
    if (userIds.length === 0) return

    try {
      await Promise.all(
        userIds.map((userId) =>
          fetchJson(
            `/owner/users/${userId}/reset-password`,
            {
              method: "POST",
              body: JSON.stringify({ sendEmail: true, reason: "Admin password reset" }),
            },
            "Failed to reset password."
          )
        )
      )

      toast({
        title: "Success",
        description: `Password reset initiated for ${userIds.length} user(s).`,
      })
      setSelectedUsers([])
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to reset passwords."),
        variant: "destructive",
      })
    }
  }

  const welcomeEligibleUsers = useMemo(
    () => allFilteredUsers.filter((user) => String(user.email || "").includes("@")),
    [allFilteredUsers]
  )

  const welcomeVisibleUsers = useMemo(() => {
    const query = welcomeSearch.trim().toLowerCase()
    if (!query) return welcomeEligibleUsers
    return welcomeEligibleUsers.filter((user) =>
      [getUserName(user), getEmployeeCode(user), user.email, getTenantName(user, tenants)]
        .join(" ")
        .toLowerCase()
        .includes(query)
    )
  }, [welcomeEligibleUsers, tenants, welcomeSearch])

  const welcomeAllVisibleSelected =
    welcomeVisibleUsers.length > 0 && welcomeVisibleUsers.every((user) => selectedUsers.includes(user.id))

  async function sendWelcomeEmail(userIds: number[]) {
    if (userIds.length === 0) {
      toast({
        title: "Error",
        description: "Select at least one user using the checkboxes, or click Select all.",
        variant: "destructive",
      })
      return
    }

    setWelcomeSending(true)
    try {
      const json = await fetchJson(
        "/owner/users/welcome-email",
        {
          method: "POST",
          body: JSON.stringify({ userIds }),
        },
        "Failed to send welcome email."
      )
      toast({
        title: "Success",
        description: json?.message || `Welcome email sent to ${userIds.length} user(s).`,
      })
      setSelectedUsers([])
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to send welcome email."),
        variant: "destructive",
      })
    } finally {
      setWelcomeSending(false)
    }
  }

  async function assignRole(user: User, roleId: string) {
    const id = Number(roleId)
    if (!Number.isFinite(id)) return

    try {
      await fetchJson(
        `/owner/users/${user.id}`,
        {
          method: "PUT",
          body: JSON.stringify({ roles: [id] }),
        },
        "Failed to update user role."
      )
      toast({ title: "Success", description: "User role updated successfully." })
      loadUsers(usersMeta.currentPage)
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to update user role."),
        variant: "destructive",
      })
    }
  }

  async function createRole() {
    const name = newRoleName.trim()
    const description = newRoleDescription.trim()

    if (!name) {
      setRoleError("Role name is required.")
      return
    }

    if (roles.some((role) => normalizeRoleName(role.name) === normalizeRoleName(name))) {
      setRoleError("Role with this name already exists.")
      return
    }

    try {
      await fetchJson(
        "/roles",
        {
          method: "POST",
          body: JSON.stringify({ name, description }),
        },
        "Failed to create role."
      )
      setNewRoleName("")
      setNewRoleDescription("")
      toast({ title: "Success", description: "Role created successfully." })
      loadRoles()
    } catch (error) {
      setRoleError(getMessage(error, "Failed to create role."))
    }
  }

  async function saveRole() {
    if (!editingRole) return
    if (isProtectedRole(editingRole)) {
      setRoleError("Default roles cannot be edited.")
      return
    }

    const duplicate = roles.some(
      (role) => role.id !== editingRole.id && normalizeRoleName(role.name) === normalizeRoleName(editingRole.name)
    )

    if (duplicate) {
      setRoleError("Role with this name already exists.")
      return
    }

    try {
      await fetchJson(
        `/roles/${editingRole.id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            name: editingRole.name.trim(),
            description: editingRole.description?.trim() || null,
          }),
        },
        "Failed to update role."
      )
      setEditingRole(null)
      toast({ title: "Success", description: "Role updated successfully." })
      loadRoles()
    } catch (error) {
      setRoleError(getMessage(error, "Failed to update role."))
    }
  }

  async function loadPermissionsMatrix(roleId: number) {
    setSelectedRoleId(roleId)
    setPermissionsLoading(true)
    try {
      const json = await fetchJson(`/roles/${roleId}/permissions-matrix`, undefined, "Failed to load permissions matrix.")
      const root = (json?.data ?? json) as Record<string, unknown>
      const next: Record<string, PermissionFlags> = {}

      function collect(items: unknown[]) {
        items.forEach((raw) => {
          const item = raw as Record<string, unknown>
          if (item.slug && item.permissions) next[String(item.slug)] = item.permissions as PermissionFlags
          if (Array.isArray(item.children)) collect(item.children)
        })
      }

      if (Array.isArray(root.resources)) collect(root.resources)

      if (Object.keys(next).length === 0) {
        const role = roles.find((candidate) => candidate.id === roleId)
        role?.permissions?.forEach((permission) => {
          const slug = permission.resource?.slug
          if (!slug) return
          next[slug] = {
            can_view: permission.canView,
            can_create: permission.canCreate,
            can_edit: permission.canEdit,
            can_delete: permission.canDelete,
            can_import: permission.canImport,
            can_export: permission.canExport,
            is_mandatory: permission.isMandatory,
          }
        })
      }

      setPermissionsMatrix(next)
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to load permissions matrix."),
        variant: "destructive",
      })
      setPermissionsMatrix({})
    } finally {
      setPermissionsLoading(false)
    }
  }

  function setPermission(resourceSlug: string, field: keyof PermissionFlags, checked: boolean) {
    setPermissionsMatrix((current) => ({
      ...current,
      [resourceSlug]: {
        ...current[resourceSlug],
        [field]: checked,
      },
    }))
  }

  function setParentPermission(resource: Resource, field: keyof PermissionFlags, checked: boolean) {
    setPermission(resource.slug, field, checked)

    const updateChildren = (parentId: number) => {
      resources
        .filter((item) => item.parentId === parentId)
        .forEach((child) => {
          setPermission(child.slug, field, checked)
          updateChildren(child.id)
        })
    }

    updateChildren(resource.id)
  }

  async function savePermissions() {
    if (!selectedRole) return

    const permissions = Object.entries(permissionsMatrix)
      .map(([slug, flags]) => {
        const resource = resources.find((item) => item.slug === slug)
        if (!resource) return null

        return {
          resourceId: resource.id,
          canView: Boolean(flags.can_view),
          canCreate: Boolean(flags.can_create),
          canEdit: Boolean(flags.can_edit),
          canDelete: Boolean(flags.can_delete),
          canImport: Boolean(flags.can_import),
          canExport: Boolean(flags.can_export),
          isMandatory: Boolean(flags.is_mandatory),
          canApprove: false,
          canBulkEdit: false,
        }
      })
      .filter(Boolean)

    try {
      await fetchJson(
        `/roles/${selectedRole.id}/permissions`,
        {
          method: "PUT",
          body: JSON.stringify({ permissions }),
        },
        "Failed to save permissions."
      )
      toast({ title: "Success", description: `Permissions saved successfully for ${selectedRole.name}.` })
    } catch (error) {
      toast({
        title: "Error",
        description: getMessage(error, "Failed to save permissions."),
        variant: "destructive",
      })
    }
  }

  function roleBadges(user: User) {
    if (user.roles.length > 0) {
      const visible = user.roles.slice(0, 2)
      return (
        <div className="flex flex-wrap gap-1">
          {visible.map((role) => (
            <Badge
              key={`${role.id}-${role.slug}`}
              variant={role.isSuperAdmin ? "outline" : role.isDefault ? "default" : "secondary"}
              className={role.isSuperAdmin ? "border-red-200 bg-red-50 text-red-700" : ""}
            >
              {role.name}
            </Badge>
          ))}
          {user.roles.length > visible.length && <Badge variant="outline">+{user.roles.length - visible.length}</Badge>}
        </div>
      )
    }

    return <Badge variant="outline">{user.role || "No roles"}</Badge>
  }

  function statusBadge(status: UserStatus) {
    if (status === "active") return <Badge className="bg-green-600 text-white">Active</Badge>
    if (status === "suspended") return <Badge className="border-red-200 bg-red-50 text-red-700">Suspended</Badge>
    return <Badge variant="secondary">Inactive</Badge>
  }

  function toggleSelectUser(userId: number, checked: boolean) {
    const id = Number(userId)
    if (!Number.isFinite(id)) return
    setSelectedUsers((current) => (checked ? [...new Set([...current, id])] : current.filter((item) => item !== id)))
  }

  function visibleResources() {
    const topLevel = resources.filter((resource) => resource.level === 0)
    const result: Resource[] = []

    function walk(resource: Resource) {
      result.push(resource)
      if (!expandedItems.has(resource.id)) return
      resources
        .filter((child) => child.parentId === resource.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(walk)
    }

    topLevel.sort((a, b) => a.sortOrder - b.sortOrder).forEach(walk)
    return result
  }

  return (
    <>
      <SEO title="User Management" description="Internal user management console." />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground mt-1">Manage platform users, roles, and access.</p>
        </div>

        {(apiError || roleError) && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {apiError || roleError}
          </div>
        )}

        <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap gap-1 p-1">
            <TabsTrigger value="users" className="flex-1 min-w-[120px]">Users</TabsTrigger>
            <TabsTrigger value="welcome-email" className="flex-1 min-w-[120px]">Welcome Email</TabsTrigger>
            <TabsTrigger value="roles" className="flex-1 min-w-[120px]">Roles</TabsTrigger>
            <TabsTrigger value="permissions" className="flex-1 min-w-[120px]">Permissions Matrix</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <Button onClick={openCreateUserForm}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add User
                </Button>
                <Button
                  variant="outline"
                  onClick={() => resetPassword(selectedUsers.length > 0 ? selectedUsers : users.map((user) => user.id))}
                  disabled={users.length === 0}
                >
                  <Key className="mr-2 h-4 w-4" />
                  Reset Passwords ({selectedUsers.length > 0 ? selectedUsers.length : users.length})
                </Button>
                {selectedUsers.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])}>
                    Clear Selection
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={() => loadUsers(usersMeta.currentPage)} disabled={usersLoading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${usersLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            {showUserForm && (
              <Card>
                <CardHeader>
                  <CardTitle>{editingUser ? "Edit User" : "Add User"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Tenant</div>
                      <select
                        className={`${selectClassName} w-full`}
                        value={newUser.tenantId}
                        onChange={(event) => setNewUser((current) => ({ ...current, tenantId: event.target.value }))}
                        disabled={Boolean(editingUser)}
                      >
                        <option value="" disabled>
                          Select tenant
                        </option>
                        {tenants.map((tenant) => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Status</div>
                      <select
                        className={`${selectClassName} w-full`}
                        value={newUser.status}
                        onChange={(event) => setNewUser((current) => ({ ...current, status: event.target.value as UserStatus }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium">First Name</div>
                      <Input
                        value={newUser.firstName}
                        onChange={(event) => setNewUser((current) => ({ ...current, firstName: event.target.value }))}
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Last Name</div>
                      <Input
                        value={newUser.lastName}
                        onChange={(event) => setNewUser((current) => ({ ...current, lastName: event.target.value }))}
                        placeholder="Last name"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Email</div>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(event) => setNewUser((current) => ({ ...current, email: event.target.value }))}
                        placeholder="name@company.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Password</div>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(event) => setNewUser((current) => ({ ...current, password: event.target.value }))}
                        placeholder={editingUser ? "Leave blank to keep current password" : "Temporary password"}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm md:col-span-2">
                      <input
                        type="checkbox"
                        checked={newUser.isSuperAdmin}
                        onChange={(event) => setNewUser((current) => ({ ...current, isSuperAdmin: event.target.checked }))}
                      />
                      Give this user Super Admin rights
                    </label>
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowUserForm(false)
                        resetUserForm()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={saveUser}>{editingUser ? "Update User" : "Save User"}</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    Total <Users className="h-4 w-4" />
                  </div>
                  <div className="mt-1 text-2xl font-semibold">{usersMeta.total}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    Active <UserCheck className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-green-600">{activeCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    Inactive <UserX className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-red-600">{inactiveCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    Roles <Shield className="h-4 w-4" />
                  </div>
                  <div className="mt-1 text-2xl font-semibold">{roles.length}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <div className="relative min-w-[220px] flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or employee code..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <select className={selectClassName} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <select className={selectClassName} value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                    <option value="all">All Roles</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.slug}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("")
                      setStatusFilter("all")
                      setRoleFilter("all")
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto rounded-lg border bg-white">
                  <table className="w-full min-w-[1100px] text-sm">
                    <thead className="bg-muted/30">
                      <tr className="text-left">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === users.length && users.length > 0}
                            onChange={(event) => setSelectedUsers(event.target.checked ? users.map((user) => user.id) : [])}
                          />
                        </th>
                        <th className="px-4 py-3 font-semibold">User</th>
                        <th className="px-4 py-3 font-semibold">Tenant Name</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold">Roles</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Last Login</th>
                        <th className="px-4 py-3 font-semibold">Created</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersLoading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                            Loading users...
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.id} className="border-t">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(event) => toggleSelectUser(user.id, event.target.checked)}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-sm font-medium text-white">
                                  {getInitial(user)}
                                </div>
                                <div>
                                  <div className="font-medium">{getUserName(user)}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {getEmployeeCode(user) || `ID: ${user.id}`}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">{getTenantName(user, tenants)}</td>
                            <td className="px-4 py-3 font-mono text-xs">{user.email}</td>
                            <td className="px-4 py-3">{roleBadges(user)}</td>
                            <td className="px-4 py-3">{statusBadge(user.status)}</td>
                            <td className="px-4 py-3">{formatDate(user.last_login_at || user.lastLoginAt)}</td>
                            <td className="px-4 py-3">{formatDate(user.created_at || user.createdAt).replace("Never", "Unknown")}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <select
                                  className="h-9 max-w-[150px] rounded-md border border-input bg-background px-2 text-xs"
                                  value={user.roles[0]?.id?.toString() || ""}
                                  onChange={(event) => assignRole(user, event.target.value)}
                                  title="Assign role"
                                >
                                  <option value="" disabled>
                                    Role
                                  </option>
                                  {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                      {role.name}
                                    </option>
                                  ))}
                                </select>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditUserForm(user)} title="Edit user">
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => resetPassword([user.id])} title="Reset password">
                                  <Key className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => updateUserStatus(user)} title="Toggle status">
                                  {user.status === "active" ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {usersMeta.total > usersMeta.perPage && (
                  <div className="flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-muted-foreground">
                      Showing {Math.min((usersMeta.currentPage - 1) * usersMeta.perPage + 1, usersMeta.total)} to{" "}
                      {Math.min(usersMeta.currentPage * usersMeta.perPage, usersMeta.total)} of {usersMeta.total} users
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={usersMeta.currentPage <= 1 || usersLoading}
                        onClick={() => loadUsers(usersMeta.currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {usersMeta.currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={usersMeta.currentPage >= totalPages || usersLoading}
                        onClick={() => loadUsers(usersMeta.currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="welcome-email" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Welcome Email
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    Select users, then send the User Welcome Email template. Username is the employee code.
                    A new password is generated, saved on the account, and included in the email.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const visibleIds = welcomeVisibleUsers.map((user) => user.id)
                      setSelectedUsers((current) =>
                        welcomeAllVisibleSelected
                          ? current.filter((id) => !visibleIds.includes(id))
                          : [...new Set([...current, ...visibleIds])]
                      )
                    }}
                    disabled={welcomeVisibleUsers.length === 0 || welcomeSending}
                  >
                    {welcomeAllVisibleSelected ? "Clear visible" : "Select all"}
                  </Button>
                  <Button
                    onClick={() => sendWelcomeEmail(selectedUsers)}
                    disabled={welcomeSending || selectedUsers.length === 0}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {welcomeSending ? "Sending..." : `Send Welcome Email (${selectedUsers.length})`}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
                  <div className="font-medium">Template preview</div>
                  <div className="text-muted-foreground">Subject: Welcome to HRMS — your login details</div>
                  <div className="rounded-md border bg-white p-4 space-y-2 leading-relaxed max-h-48 overflow-auto">
                    <p>Dear [Employee Full Name],</p>
                    <p>Welcome to the HRMS portal. Your login details are below.</p>
                    <p><strong>Username:</strong> Employee Code</p>
                    <p><strong>Password:</strong> generated password</p>
                    <p>Please sign in using your employee code as the username. We recommend changing this password after your first login.</p>
                    <p>If you did not expect this email, please contact your administrator.</p>
                    <p>Regards,<br />HR Team</p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    {selectedUsers.length} selected of {welcomeEligibleUsers.length} user(s)
                  </div>
                  {selectedUsers.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])}>
                      Clear Selection
                    </Button>
                  )}
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={welcomeSearch}
                    onChange={(event) => setWelcomeSearch(event.target.value)}
                    placeholder="Search by name, employee code, email, or tenant"
                    className="pl-9"
                  />
                </div>

                <div className="overflow-auto rounded-lg border bg-white max-h-[420px]">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="bg-muted/30 sticky top-0">
                      <tr className="text-left">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={welcomeAllVisibleSelected}
                            onChange={(event) => {
                              const visibleIds = welcomeVisibleUsers.map((user) => user.id)
                              setSelectedUsers((current) =>
                                event.target.checked
                                  ? [...new Set([...current, ...visibleIds])]
                                  : current.filter((id) => !visibleIds.includes(id))
                              )
                            }}
                          />
                        </th>
                        <th className="px-4 py-3 font-semibold">User</th>
                        <th className="px-4 py-3 font-semibold">Username</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold">Tenant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {welcomeVisibleUsers.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                            No users found. Use the Users tab filters first, or clear this search.
                          </td>
                        </tr>
                      ) : (
                        welcomeVisibleUsers.map((user) => (
                          <tr key={user.id} className="border-t">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(event) => toggleSelectUser(user.id, event.target.checked)}
                              />
                            </td>
                            <td className="px-4 py-3 font-medium">{getUserName(user)}</td>
                            <td className="px-4 py-3 font-mono text-xs">{getEmployeeCode(user) || "—"}</td>
                            <td className="px-4 py-3 font-mono text-xs">{user.email}</td>
                            <td className="px-4 py-3">{getTenantName(user, tenants)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles" className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Role Management</h2>
              <p className="text-sm text-muted-foreground">Create and manage roles for your organization.</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{editingRole ? "Edit Role" : "Create Role"}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                  <Input
                    value={editingRole ? editingRole.name : newRoleName}
                    onChange={(event) =>
                      editingRole
                        ? setEditingRole({ ...editingRole, name: event.target.value })
                        : setNewRoleName(event.target.value)
                    }
                    placeholder="Role name"
                    disabled={Boolean(editingRole && isProtectedRole(editingRole))}
                  />
                  <Input
                    value={editingRole ? editingRole.description || "" : newRoleDescription}
                    onChange={(event) =>
                      editingRole
                        ? setEditingRole({ ...editingRole, description: event.target.value })
                        : setNewRoleDescription(event.target.value)
                    }
                    placeholder="Description"
                    disabled={Boolean(editingRole && isProtectedRole(editingRole))}
                  />
                  <div className="flex gap-2">
                    {editingRole ? (
                      <>
                        <Button onClick={saveRole} disabled={isProtectedRole(editingRole)}>
                          Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingRole(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={createRole}>Create Role</Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                {rolesLoading ? (
                  <div className="py-8 text-center text-muted-foreground">Loading roles...</div>
                ) : roles.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">No roles found.</div>
                ) : (
                  <div className="space-y-3">
                    {roles.map((role) => {
                      const protectedRole = isProtectedRole(role)
                      return (
                        <div key={role.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="font-medium">{role.name}</div>
                            <div className="text-sm text-muted-foreground">{role.description || "No description"}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Badge variant={role.isSuperAdmin ? "outline" : protectedRole ? "secondary" : "default"}>
                                {role.isSuperAdmin ? "Super Admin" : protectedRole ? "Default Role" : "Regular Role"}
                              </Badge>
                              <Badge variant="outline">{role.status}</Badge>
                              <Badge variant="secondary">{role.userCount ?? 0} users</Badge>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingRole(role)}
                            disabled={protectedRole}
                            title={protectedRole ? "Default roles cannot be edited" : "Edit role"}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <select
                className={`${selectClassName} w-full sm:w-[280px]`}
                value={selectedRoleId?.toString() || ""}
                onChange={(event) => loadPermissionsMatrix(Number(event.target.value))}
              >
                <option value="" disabled>
                  Select a role
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <Button onClick={savePermissions} disabled={!selectedRole || permissionsLoading}>
                <Save className="mr-2 h-4 w-4" />
                Save Permissions
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {!selectedRole ? (
                  <div className="p-8 text-center text-muted-foreground">Select a role to manage permissions.</div>
                ) : permissionsLoading ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                    Loading resources...
                  </div>
                ) : resources.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Layers className="mx-auto mb-2 h-10 w-10" />
                    No resources found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[900px]">
                      <div className="grid grid-cols-8 gap-4 border-b bg-muted/30 p-3 text-sm font-medium">
                        <div>Resource Hierarchy</div>
                        <div className="text-center">View</div>
                        <div className="text-center">Create</div>
                        <div className="text-center">Edit</div>
                        <div className="text-center">Import</div>
                        <div className="text-center">Export</div>
                        <div className="text-center">Mandatory</div>
                        <div className="text-center">Type</div>
                      </div>
                      {visibleResources().map((resource) => {
                        const hasChildren = resources.some((item) => item.parentId === resource.id)
                        const isParent = hasChildren && resource.type !== "field"
                        const flags = permissionsMatrix[resource.slug] || {}
                        return (
                          <div key={resource.id} className="grid grid-cols-8 gap-4 border-b p-3 text-sm hover:bg-muted/20">
                            <div className="flex items-center" style={{ paddingLeft: `${resource.level * 1.25}rem` }}>
                              {hasChildren ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="mr-2 h-6 w-6"
                                  onClick={() => {
                                    setExpandedItems((current) => {
                                      const next = new Set(current)
                                      if (next.has(resource.id)) next.delete(resource.id)
                                      else next.add(resource.id)
                                      return next
                                    })
                                  }}
                                >
                                  {expandedItems.has(resource.id) ? "-" : "+"}
                                </Button>
                              ) : (
                                <span className="mr-2 h-6 w-6" />
                              )}
                              <span className={resource.level === 0 ? "font-semibold text-blue-700" : ""}>{resource.name}</span>
                            </div>
                            {[
                              ["can_view", "View"],
                              ["can_create", "Create"],
                              ["can_edit", "Edit"],
                              ["can_import", "Import"],
                              ["can_export", "Export"],
                            ].map(([field, label]) => (
                              <div key={field} className="flex justify-center">
                                <input
                                  type="checkbox"
                                  title={label}
                                  checked={Boolean(flags[field as keyof PermissionFlags])}
                                  onChange={(event) =>
                                    isParent
                                      ? setParentPermission(resource, field as keyof PermissionFlags, event.target.checked)
                                      : setPermission(resource.slug, field as keyof PermissionFlags, event.target.checked)
                                  }
                                />
                              </div>
                            ))}
                            <div className="flex justify-center">
                              {resource.type === "field" ? (
                                <input
                                  type="checkbox"
                                  title="Mandatory"
                                  checked={Boolean(flags.is_mandatory)}
                                  onChange={(event) => setPermission(resource.slug, "is_mandatory", event.target.checked)}
                                />
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                            <div className="flex justify-center">
                              <Badge variant="outline">{resource.type}</Badge>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}

